'use client';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from 'next/link';
import { LuMinus, LuPlus } from 'react-icons/lu';

import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function FAQ({ faqs }: { faqs: TDynamicData[] }) {
  const { t } = useTranslations();
  const isDesktop = useBreakpoint('sm');

  return (
    <section
      className={cn('custom-section', 'section-padding-lg-top', 'section-padding-xl-bottom')}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start md:flex-row md:justify-between',
          'generic-gap',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-6 sm:max-w-[384px]">
          <Heading title="Máte ďalšie<br/>otázky?" />
          <Paragraph size="lg">
            Pripravili sme odpovede na to, čo nás hostia pýtajú najčastejšie. Ak vám niečo chýba,
            ozvite sa nám priamo.
          </Paragraph>
          <div className="flex w-full flex-col items-start justify-start gap-2 sm:w-fit sm:flex-row">
            <Button asChild className="w-full sm:w-fit">
              <Link href={'/rezervacia'} prefetch={false}>
                <ButtonIcon alt={t('text538')} url="/images/icons/date-primary.svg" />
                {t('text538')}
              </Link>
            </Button>
            <Button asChild className="w-full sm:w-fit" variant={'outline'}>
              <Link href={ROUTES.CONTACT()}>Kontakt</Link>
            </Button>
          </div>
        </div>
        <Accordion type="multiple" className="flex w-full flex-col items-start justify-start gap-0">
          {faqs.map((item, index) => (
            <AccordionItem
              key={item.name}
              value={item.name}
              className={cn(
                'group w-full py-4 sm:py-5 lg:py-6',
                index !== 0 && 'border-t border-t-border',
              )}
            >
              <AccordionTrigger
                hideIcon
                className="flex items-center justify-between gap-4 py-0 text-left text-base font-bold text-secondary sm:text-lg lg:text-xl"
              >
                {item.name}
                <div
                  className={cn(
                    'flex shrink-0 items-center justify-center rounded-md border border-input bg-background',
                    isDesktop ? 'size-10' : 'size-8',
                  )}
                >
                  <LuPlus
                    size={isDesktop ? 20 : 16}
                    className="text-secondary group-data-[state=open]:hidden"
                  />
                  <LuMinus
                    size={isDesktop ? 20 : 16}
                    className="text-secondary group-data-[state=closed]:hidden"
                  />
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-0 pt-3">
                <Paragraph
                  innerHtml={item?.fields[0]?.value}
                  color="secondary"
                  className="w-full"
                  size="lg"
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
